import { Plus } from "lucide-react";
import type { FaqItem } from "../utils/structuredData";

interface FaqProps {
  id?: string;
  title?: string;
  subtitle?: string;
  items: FaqItem[];
}

// Accessible, crawlable FAQ built on native <details>/<summary>. The answer text
// is always present in the DOM (even when visually collapsed), so search engines
// and AI crawlers can read every answer, and it stays keyboard-accessible with
// no JavaScript. Pair with buildFaqSchema() so the JSON-LD matches this markup.
const Faq: React.FC<FaqProps> = ({ id, title = "Frequently Asked Questions", subtitle, items }) => {
  return (
    <section id={id}>
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-text-primary mb-3 sm:mb-4">
          {title}
        </h2>
        {subtitle && (
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto px-2">
            {subtitle}
          </p>
        )}
      </div>

      <div className="space-y-3 sm:space-y-4">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-2xl border border-borderSubtle bg-surface p-5 sm:p-6 transition-colors duration-200 hover:border-primary/40 open:border-primary/40 open:bg-surfaceSoft"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-4 list-none [&::-webkit-details-marker]:hidden">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                {item.question}
              </h3>
              <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-primary/20 bg-primary/10 text-primary transition-transform duration-300 group-open:rotate-45">
                <Plus aria-hidden="true" className="w-4 h-4" />
              </span>
            </summary>
            <hr className="border-borderSubtle my-3 sm:my-4" />
            <p className="text-text-secondary leading-relaxed">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
};

export default Faq;
